import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mediaAPI, musicCreditsAPI } from '@/lib/api';
import { BrutalCard, BrutalButton, BrutalBadge } from '@/components/brutal';
import { Lock, Music, Video, Heart, Download, Play, Pause, ExternalLink, BookOpen, Calendar, Headphones, CreditCard, Clock, ChevronDown, ChevronUp, ListPlus, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import MusicDiscover from '@/components/student/MusicDiscover';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
};

const StudentMediaLibrary = ({ student }) => {
  const qc = useQueryClient();
  const [expandedItem, setExpandedItem] = useState(null);
  const player = useMusicPlayer();

  const { data: libraryData = {} } = useQuery({
    queryKey: ['student-media-library', student?.id],
    queryFn: async () => {
      const res = await mediaAPI.getStudentLibrary(student.id);
      return Array.isArray(res.data) ? { items: res.data, music_credits: 0 } : res.data;
    },
    enabled: !!student?.id,
  });

  const { data: creditsData } = useQuery({
    queryKey: ['student-music-credits', student?.id],
    queryFn: async () => (await musicCreditsAPI.getStudentCredits(student.id)).data,
    enabled: !!student?.id,
  });

  const library = libraryData.items || [];
  const musicCredits = creditsData?.music_credits ?? libraryData.music_credits ?? 0;
  const pricePerDownload = creditsData?.price_per_download ?? 0.09;
  const creditValue = (musicCredits * pricePerDownload).toFixed(2);

  const likeMut = useMutation({
    mutationFn: ({ mediaId, liked }) => mediaAPI.toggleLike(student.id, { media_id: mediaId, liked }),
    onSuccess: () => qc.invalidateQueries(['student-media-library']),
  });

  const downloadMut = useMutation({
    mutationFn: (mediaId) => mediaAPI.purchaseDownload(student.id, { media_id: mediaId }),
    onSuccess: (res) => {
      qc.invalidateQueries(['student-media-library']);
      const d = res.data;
      if (d.payment_method === 'music_credit') {
        toast.success(`Downloaded! Used 1 music credit (${d.music_credits_remaining} remaining)`);
      } else if (d.payment_method === 'wallet') {
        toast.success(`Downloaded! Charged $${d.price.toFixed(2)} from wallet`);
      } else {
        toast.success('Downloaded for free!');
      }
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Download failed'),
  });

  if (library.length === 0) {
    return (
      <div className="space-y-6">
        <BrutalCard shadow="md" className="text-center py-8" data-testid="media-library-empty">
          <Music size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-lg font-bold text-gray-500">No music yet</p>
          <p className="text-sm text-gray-400">Songs and videos from your stories will appear here!</p>
        </BrutalCard>
        <MusicDiscover student={student} />
      </div>
    );
  }

  const likedItems = library.filter(i => i.liked);
  const downloadedItems = library.filter(i => i.downloaded);

  return (
    <div className="space-y-6" data-testid="student-media-library">
      {/* Credits Banner */}
      <BrutalCard shadow="md" className="bg-gradient-to-r from-indigo-50 to-violet-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard size={22} className="text-indigo-600" />
            <div>
              <p className="font-black text-lg text-indigo-700">{musicCredits} Song Credits</p>
              <p className="text-xs text-gray-500">
                Worth <span className="font-black text-emerald-600">${creditValue}</span> &middot; ${pricePerDownload} per song
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">{downloadedItems.length} downloaded</p>
            <p className="text-xs text-gray-500">{library.length} total songs heard</p>
          </div>
        </div>
      </BrutalCard>

      {/* Favorites */}
      {likedItems.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black uppercase flex items-center gap-2">
              <Heart size={20} className="text-rose-500 fill-rose-500" /> My Favorites ({likedItems.length})
            </h3>
            <BrutalButton variant="indigo" size="sm" onClick={() => player.playAll(likedItems.filter(i => i.file_url))}>
              <PlayCircle size={14} className="mr-1" /> Play All
            </BrutalButton>
          </div>
          <div className="grid gap-3">
            {likedItems.map(item => (
              <SongCard key={`fav-${item.media_id}`} item={item} player={player}
                onLike={likeMut.mutate} onDownload={downloadMut.mutate} musicCredits={musicCredits}
                expanded={expandedItem === item.media_id} onToggleExpand={() => setExpandedItem(expandedItem === item.media_id ? null : item.media_id)} />
            ))}
          </div>
        </>
      )}

      {/* All Music */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-black uppercase">All Music ({library.length})</h3>
        <BrutalButton variant="indigo" size="sm" onClick={() => player.playAll(library.filter(i => i.file_url))}>
          <PlayCircle size={14} className="mr-1" /> Play All
        </BrutalButton>
      </div>
      <div className="grid gap-3">
        {library.map(item => (
          <SongCard key={item.media_id} item={item} player={player}
            onLike={likeMut.mutate} onDownload={downloadMut.mutate} musicCredits={musicCredits}
            expanded={expandedItem === item.media_id} onToggleExpand={() => setExpandedItem(expandedItem === item.media_id ? null : item.media_id)} />
        ))}
      </div>

      <div style={{ paddingBottom: '80px' }}>
        <MusicDiscover student={student} />
      </div>
    </div>
  );
};

const SongCard = ({ item, player, onLike, onDownload, musicCredits, expanded, onToggleExpand }) => {
  const [showVideo, setShowVideo] = useState(false);
  const ytId = extractYouTubeId(item.youtube_url);
  const isAudio = item.media_type === 'audio' || item.source === 'upload';
  const isCurrentTrack = player.currentTrack?.media_id === item.media_id;
  const isPlayingThis = isCurrentTrack && player.isPlaying;
  const locked = !!item.locked;

  return (
    <BrutalCard shadow="sm" data-testid={`media-item-${item.media_id}`} className={locked ? 'opacity-90' : ''}>
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 flex items-center justify-center border-4 border-black flex-shrink-0 cursor-pointer transition-all hover:scale-105 ${isPlayingThis ? 'bg-indigo-200' : item.media_type === 'video' ? 'bg-rose-100' : 'bg-indigo-100'}`}
          onClick={() => locked ? toast.info(item.lock_message) : isAudio ? (isCurrentTrack ? player.togglePlay() : player.playTrack(item)) : setShowVideo(!showVideo)}>
          {locked ? <Lock size={22} className="text-amber-600" /> : isAudio ? (
            isPlayingThis ? <Pause size={24} className="text-indigo-600" /> : <Play size={24} className="text-indigo-600" />
          ) : (
            <Video size={24} className="text-rose-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-base truncate">{item.title}</p>
          <p className="text-xs text-gray-500">{item.artist || 'Unknown Artist'}</p>
          {locked && <p className="text-xs font-bold text-amber-700 mt-0.5" data-testid={`lock-note-${item.media_id}`}>{item.lock_message}</p>}
          <p className="text-xs text-gray-400">
            <Headphones size={10} className="inline mr-1" />{item.listen_count || 0} plays
            {item.stories_appeared_in?.length > 0 && (
              <> &middot; <BookOpen size={10} className="inline mr-1" />{item.stories_appeared_in.length} {item.stories_appeared_in.length === 1 ? 'story' : 'stories'}</>
            )}
          </p>
        </div>
        <div className="flex gap-1.5 items-center flex-shrink-0">
          {isAudio && !isCurrentTrack && (
            <button onClick={() => { player.addToQueue(item); toast.success(`"${item.title}" added to queue`); }}
              className="p-2 text-gray-300 hover:text-indigo-500 transition-all" title="Add to queue">
              <ListPlus size={18} />
            </button>
          )}
          <button onClick={() => onLike({ mediaId: item.media_id, liked: !item.liked })}
            className="p-2 transition-all hover:scale-110" data-testid={`like-${item.media_id}`}>
            <Heart size={18} className={item.liked ? 'text-rose-500 fill-rose-500' : 'text-gray-300'} />
          </button>
          {!item.downloaded ? (
            <BrutalButton variant="emerald" size="sm" onClick={() => {
              const msg = musicCredits > 0
                ? `Download "${item.title}" using 1 music credit?`
                : `Download "${item.title}" for $${item.price_per_download?.toFixed(2)}?`;
              if (window.confirm(msg)) onDownload(item.media_id);
            }} data-testid={`download-${item.media_id}`}>
              <Download size={14} className="mr-1" />
              {musicCredits > 0 ? 'Credit' : `$${item.price_per_download?.toFixed(2) || '0.09'}`}
            </BrutalButton>
          ) : (
            <BrutalBadge variant="emerald" size="sm">Downloaded</BrutalBadge>
          )}
          <button onClick={onToggleExpand} className="p-1.5 text-gray-400 hover:text-gray-700 transition-all">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Expanded Detail View */}
      {expanded && (
        <div className="mt-3 pt-3 border-t-2 border-gray-100 space-y-2">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="font-bold text-gray-500 uppercase text-[10px]">Artist</p>
              <p className="font-bold">
                {item.artist || 'Unknown'}
                {item.artist_url && (
                  <a href={item.artist_url} target="_blank" rel="noopener noreferrer"
                    className="ml-1 text-indigo-500 hover:text-indigo-700 inline-flex items-center">
                    <ExternalLink size={10} />
                  </a>
                )}
              </p>
            </div>
            {item.album && (
              <div>
                <p className="font-bold text-gray-500 uppercase text-[10px]">Album</p>
                <p className="font-bold">{item.album}</p>
              </div>
            )}
            <div>
              <p className="font-bold text-gray-500 uppercase text-[10px]">First Heard</p>
              <p className="font-bold">{formatDate(item.first_heard)}</p>
            </div>
            <div>
              <p className="font-bold text-gray-500 uppercase text-[10px]">Times Played</p>
              <p className="font-bold">{item.listen_count || 0}</p>
            </div>
            {item.downloaded && (
              <div>
                <p className="font-bold text-gray-500 uppercase text-[10px]">Downloaded</p>
                <p className="font-bold">{formatDate(item.download_date)}</p>
              </div>
            )}
            {item.downloaded && item.payment_method && (
              <div>
                <p className="font-bold text-gray-500 uppercase text-[10px]">Paid With</p>
                <p className="font-bold capitalize">{item.payment_method === 'music_credit' ? 'Music Credit' : item.payment_method}</p>
              </div>
            )}
          </div>
          {item.stories_appeared_in?.length > 0 && (
            <div>
              <p className="font-bold text-gray-500 uppercase text-[10px] mb-1">Appeared In Stories</p>
              <div className="flex flex-wrap gap-1">
                {item.stories_appeared_in.map((story, i) => (
                  <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold">
                    <BookOpen size={10} className="inline mr-1" />{story}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showVideo && ytId && (
        <div className="mt-3 aspect-video w-full">
          <iframe src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1`}
            className="w-full h-full border-4 border-black" allow="autoplay; encrypted-media" allowFullScreen
            title={item.title} data-testid={`video-player-${item.media_id}`} />
        </div>
      )}
    </BrutalCard>
  );
};

export default StudentMediaLibrary;
